(() => {
  'use strict';

  const selectors = {
    section: '[data-ksc-section]',
    filterShell: '[data-ksc-filter-shell]',
    filterPanel: '[data-ksc-filter-panel]',
    filterOpen: '[data-ksc-filter-open]',
    filterClose: '[data-ksc-filter-close]',
    filterForm: '[data-ksc-filter-form]',
    sortForm: '[data-ksc-sort-form]',
    sortSelect: '[data-ksc-sort-select]',
    ajaxLink: '[data-ksc-ajax-link]',
    grid: '[data-ksc-grid]',
    gridItem: '[data-ksc-grid-item]',
    pagination: '[data-ksc-pagination]',
    loadMore: '[data-ksc-load-more]',
    loadMoreLabel: '[data-ksc-load-more-label]',
    loadedCount: '[data-ksc-loaded-count]',
    progress: '[data-ksc-progress]',
    productCount: '[data-ksc-product-count]',
    toolbar: '[data-ksc-toolbar]',
    catalogMain: '.ksc__catalog-main',
    filterGroups: '.ksc__filter-groups',
    filterActions: '.ksc__filter-actions',
    filterCount: '.ksc__filter-count',
    formattedNumber: '[data-ksc-number]',
    results: '[data-ksc-results]',
    loadingStatus: '[data-ksc-loading-status]',
    cardRating: '[data-ksc-card-rating]',
    descriptionWrap: '[data-ksc-description-wrap]',
    descriptionToggle: '[data-ksc-description-toggle]',
    descriptionLabel: '[data-ksc-description-label]',
  };

  const desktopQuery = window.matchMedia('(min-width: 990px)');

  const utilitySelectors = [
  /* Umino / Shopify localization */
  '.lang-curentcy',
  'localization-form',
  '.shopify-localization-form',

  /* G | Translate / GTranslate */
  '.gtranslate_wrapper',
  '#gtranslate_wrapper',
  '.gt_switcher_wrapper',
  '.gt_switcher',
  '.gt_float_switcher',
  '.gt_container',
  '.gt_selector',
  '.switcher.notranslate',
  '[class*="gt_switcher"]',
  '[class*="gtranslate"]',
  'iframe[src*="gtranslate"]',
  'iframe[title*="translate" i]',
  'iframe[title*="language" i]',

  /* Other translator fallbacks */
  '.ly-switcher-wrapper',
  '.weglot-container',
  '.weglot_switcher',
  '.gtranslate_wrapper',
  '#google_translate_element',
  '.tlab-switcher',
  '#transcySwitcherContainer',
  '.transcy-switcher-manual',
  '.etranslate-language-switcher',
  '.langify-switcher',
].join(',');


  const zeroReviewPattern =
    /(?:new\s+arrival|be\s+the\s+first\s+to\s+review|\b0(?:\.0+)?\s*reviews?\b)/i;

  const getSameOriginUrl = (value) => {
    try {
      const url = new URL(value, window.location.origin);

      return url.origin === window.location.origin
        ? url
        : null;
    } catch (_error) {
      return null;
    }
  };

  const getCleanUrl = (value) => {
    const url = getSameOriginUrl(value);

    if (!url) return null;

    url.searchParams.delete('section_id');

    return url;
  };

  const getSectionUrl = (value, sectionId) => {
    const url = getCleanUrl(value);

    if (!url) return null;

    url.searchParams.set('section_id', sectionId);

    return url.toString();
  };

  const getFormUrl = (form) => {
    if (!form) return null;

    const url = getSameOriginUrl(
      form.action || window.location.href,
    );

    if (!url) return null;

    url.search = '';

    new FormData(form).forEach((value, key) => {
      const cleanedValue = String(value).trim();

      if (cleanedValue !== '') {
        url.searchParams.append(key, cleanedValue);
      }
    });

    url.searchParams.delete('page');

    return url;
  };

  const formatNumber = (value) => {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return String(value ?? '');
    }

    try {
      return new Intl.NumberFormat(
        document.documentElement.lang || 'en-US',
      ).format(numericValue);
    } catch (_error) {
      return String(numericValue);
    }
  };

  const formatNumbers = (scope) => {
    scope
      .querySelectorAll?.(selectors.formattedNumber)
      .forEach((element) => {
        const value =
          element.dataset.kscNumber ||
          element.textContent;

        element.textContent = formatNumber(value);
      });
  };


  const setLoading = (root, loading) => {
    if (!root) return;

    root.classList.toggle('is-updating', loading);
    root.dataset.kscLoading = loading ? 'true' : 'false';

    root.setAttribute(
      'aria-busy',
      loading ? 'true' : 'false',
    );

    const loadingStatus =
      root.querySelector(selectors.loadingStatus);

    if (loadingStatus) {
      loadingStatus.hidden = !loading;
    }
  };

  const suppressUtilities = (suppress) => {
    document
      .querySelectorAll(utilitySelectors)
      .forEach((element) => {
        element.classList.toggle(
          'ksc-suppressed-utility',
          suppress,
        );
      });
  };

  const setFilterPageState = (open) => {
    document.documentElement.classList.toggle(
      'ksc-filter-open',
      open,
    );

    document.body.classList.toggle(
      'ksc-filter-open',
      open,
    );

    suppressUtilities(open);
  };

  const syncFilterAccessibility = (root) => {
    const shell =
      root.querySelector(selectors.filterShell);

    const panel =
      root.querySelector(selectors.filterPanel);

    const trigger =
      root.querySelector(selectors.filterOpen);

    if (!shell || !panel) return;

    const desktop = desktopQuery.matches;
    const open = shell.classList.contains('is-open');

    if (desktop) {
      shell.classList.remove('is-open');
      shell.removeAttribute('aria-hidden');

      panel.setAttribute('role', 'region');
      panel.removeAttribute('aria-modal');

      trigger?.setAttribute(
        'aria-expanded',
        'false',
      );

      setFilterPageState(false);
      return;
    }

    shell.setAttribute(
      'aria-hidden',
      open ? 'false' : 'true',
    );

    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');

    trigger?.setAttribute(
      'aria-expanded',
      open ? 'true' : 'false',
    );
  };

  const openFilter = (root) => {
    if (desktopQuery.matches) return;

    const shell =
      root.querySelector(selectors.filterShell);

    if (!shell) return;

    shell.classList.add('is-open');

    setFilterPageState(true);
    syncFilterAccessibility(root);

    window.requestAnimationFrame(() => {
      shell
        .querySelector('.ksc__filter-close')
        ?.focus({
          preventScroll: true,
        });
    });

    window.setTimeout(
      () => suppressUtilities(true),
      100,
    );

    window.setTimeout(
      () => suppressUtilities(true),
      600,
    );
  };

  const closeFilter = (
    root,
    restoreFocus = true,
  ) => {
    const shell =
      root?.querySelector(selectors.filterShell);

    if (!shell) {
      setFilterPageState(false);
      return;
    }

    const wasOpen =
      shell.classList.contains('is-open');

    shell.classList.remove('is-open');

    setFilterPageState(false);
    syncFilterAccessibility(root);

    if (wasOpen && restoreFocus) {
      root
        .querySelector(selectors.filterOpen)
        ?.focus({
          preventScroll: true,
        });
    }
  };

  const initializeFilterDrawer = (root, signal) => {
    const shell =
      root.querySelector(selectors.filterShell);

    const trigger =
      root.querySelector(selectors.filterOpen);

    if (!shell) return;

    trigger?.addEventListener(
      'click',
      () => openFilter(root),
      { signal },
    );

    shell
      .querySelectorAll(selectors.filterClose)
      .forEach((button) => {
        button.addEventListener(
          'click',
          () => closeFilter(root),
          { signal },
        );
      });

    document.addEventListener(
      'keydown',
      (event) => {
        if (
          event.key === 'Escape' &&
          shell.classList.contains('is-open')
        ) {
          event.preventDefault();
          closeFilter(root);
        }
      },
      { signal },
    );

    const updateBreakpoint = () => {
      closeFilter(root, false);
      syncFilterAccessibility(root);
    };

    desktopQuery.addEventListener(
      'change',
      updateBreakpoint,
      { signal },
    );

    syncFilterAccessibility(root);
  };

  const markZeroReviewIcons = (wrapper) => {
    wrapper
      .querySelectorAll(
        'svg, img, i, [class*="star" i], [class*="rating-icon" i]',
      )
      .forEach((element) => {
        if (
          element.classList.contains(
            'alireviews-review-star-rating',
          )
        ) {
          return;
        }

        const visibleText =
          element.textContent
            ?.replace(/\s+/g, ' ')
            .trim() || '';

        if (visibleText === '') {
          element.classList.add(
            'ksc-zero-review-star',
          );
        }
      });
  };

  const syncCardRating = (wrapper) => {
    const text =
      wrapper.textContent
        ?.replace(/\s+/g, ' ')
        .trim() || '';

    if (text === '') return;

    const isZeroReview =
      zeroReviewPattern.test(text);

    wrapper.classList.toggle(
      'is-zero-review',
      isZeroReview,
    );

    if (isZeroReview) {
      markZeroReviewIcons(wrapper);
      return;
    }

    wrapper
      .querySelectorAll('.ksc-zero-review-star')
      .forEach((element) => {
        element.classList.remove(
          'ksc-zero-review-star',
        );
      });
  };

  const syncCardRatings = (root) => {
    root
      .querySelectorAll(selectors.cardRating)
      .forEach(syncCardRating);
  };

  const initializeCardRatings = (root, signal) => {
    let frame = 0;

    const scheduleSync = () => {
      window.cancelAnimationFrame(frame);

      frame = window.requestAnimationFrame(() => {
        syncCardRatings(root);
      });
    };

    scheduleSync();

    const observer = new MutationObserver(scheduleSync);

    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    signal.addEventListener(
      'abort',
      () => {
        observer.disconnect();
        window.cancelAnimationFrame(frame);
      },
      { once: true },
    );
  };

  const initializeDescriptions = (
    scope,
    signal,
  ) => {
    scope
      .querySelectorAll(selectors.descriptionWrap)
      .forEach((wrapper) => {
        if (
          wrapper.dataset
            .kscDescriptionInitialized === 'true'
        ) {
          return;
        }

        const button =
          wrapper.querySelector(
            selectors.descriptionToggle,
          );

        const label =
          wrapper.querySelector(
            selectors.descriptionLabel,
          );

        if (!button || !label) return;

        wrapper.dataset
          .kscDescriptionInitialized = 'true';

        button.addEventListener(
          'click',
          () => {
            const expanded =
              wrapper.classList.toggle(
                'is-expanded',
              );

            button.setAttribute(
              'aria-expanded',
              expanded ? 'true' : 'false',
            );

            label.textContent =
              expanded
                ? 'Read less'
                : 'Read more';
          },
          { signal },
        );
      });
  };

  const updatePagination = (
    root,
    sourceRoot,
  ) => {
    const currentPagination =
      root.querySelector(selectors.pagination);

    const newPagination =
      sourceRoot.querySelector(selectors.pagination);

    if (newPagination) {
      currentPagination?.replaceWith(
        document.importNode(
          newPagination,
          true,
        ),
      );

      formatNumbers(root);
      return;
    }

    currentPagination?.remove();
  };

  const updateProgress = (
    root,
    loadedValue,
  ) => {
    const results =
      root.querySelector(selectors.results);

    const count =
      root.querySelector(selectors.loadedCount);

    const progress =
      root.querySelector(selectors.progress);

    const total = Number(
      results?.dataset.totalProducts || 0,
    );

    const loaded = Number(
      loadedValue ??
      results?.dataset.loadedProducts ??
      0,
    );

    if (results) {
      results.dataset.loadedProducts =
        String(loaded);
    }

    if (count && total > 0) {
      count.innerHTML =
        `Showing <strong>${formatNumber(
          loaded,
        )}</strong> of <strong>${formatNumber(
          total,
        )}</strong> products`;
    }

    if (progress && total > 0) {
      progress.style.width =
        `${Math.min(
          100,
          (loaded / total) * 100,
        )}%`;
    }
  };

  const announceProductsUpdated = (
    root,
    detail = {},
  ) => {
    const eventDetail = {
      sectionId:
        root?.dataset.sectionId || '',
      ...detail,
    };

    root?.dispatchEvent(
      new CustomEvent(
        'ksc:products-updated',
        {
          bubbles: true,
          detail: eventDetail,
        },
      ),
    );

    document.dispatchEvent(
      new CustomEvent(
        'ksc:products-updated',
        {
          detail: eventDetail,
        },
      ),
    );
  };

  const scrollToCollectionToolbar = async (root) => {
    const toolbar = root?.querySelector(selectors.toolbar);

    if (!toolbar) return;

    toolbar.scrollIntoView({
      behavior: 'auto',
      block: 'start',
    });

    await new Promise((resolve) => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(resolve);
      });
    });
  };

  const syncProductCount = (root, sourceRoot) => {
    const currentCount =
      root.querySelector(selectors.productCount);

    const sourceCount =
      sourceRoot.querySelector(selectors.productCount);

    if (!currentCount || !sourceCount) return;

    currentCount.innerHTML = sourceCount.innerHTML;
    formatNumbers(currentCount);
  };

  const syncFilterTrigger = (root, sourceRoot) => {
    const currentTrigger =
      root.querySelector(selectors.filterOpen);

    const sourceTrigger =
      sourceRoot.querySelector(selectors.filterOpen);

    if (!currentTrigger || !sourceTrigger) return;

    const expanded =
      currentTrigger.getAttribute('aria-expanded') || 'false';

    currentTrigger.innerHTML = sourceTrigger.innerHTML;
    currentTrigger.setAttribute('aria-expanded', expanded);
  };

  const getFilterGroupLabel = (details) =>
    details
      ?.querySelector('summary > span:first-child')
      ?.textContent
      ?.replace(/\s+/g, ' ')
      .trim() || '';

  const syncFilterForm = (root, sourceRoot) => {
    const currentForm =
      root.querySelector(selectors.filterForm);

    const sourceForm =
      sourceRoot.querySelector(selectors.filterForm);

    if (!currentForm || !sourceForm) return;

    const currentGroups =
      currentForm.querySelector(selectors.filterGroups);

    const sourceGroups =
      sourceForm.querySelector(selectors.filterGroups);

    const currentActions =
      currentForm.querySelector(selectors.filterActions);

    const sourceActions =
      sourceForm.querySelector(selectors.filterActions);

    const openLabels = new Set(
      [...(currentGroups?.querySelectorAll('details[open]') || [])]
        .map(getFilterGroupLabel)
        .filter(Boolean),
    );

    if (currentGroups && sourceGroups) {
      const replacement = document.importNode(
        sourceGroups,
        true,
      );

      replacement
        .querySelectorAll('details')
        .forEach((details) => {
          const label = getFilterGroupLabel(details);

          if (openLabels.has(label)) {
            details.open = true;
          }
        });

      currentGroups.replaceWith(replacement);
    }

    if (currentActions && sourceActions) {
      currentActions.replaceWith(
        document.importNode(sourceActions, true),
      );
    }

    const currentSortInput =
      currentForm.querySelector('input[name="sort_by"]');

    const sourceSortInput =
      sourceForm.querySelector('input[name="sort_by"]');

    if (currentSortInput && sourceSortInput) {
      currentSortInput.value = sourceSortInput.value;
    }
  };

  const syncSortForm = (root, sourceRoot) => {
    const currentForm =
      root.querySelector(selectors.sortForm);

    const sourceForm =
      sourceRoot.querySelector(selectors.sortForm);

    if (!currentForm || !sourceForm) return;

    currentForm
      .querySelectorAll('input[type="hidden"]')
      .forEach((input) => input.remove());

    const hiddenInputs = document.createDocumentFragment();

    sourceForm
      .querySelectorAll('input[type="hidden"]')
      .forEach((input) => {
        hiddenInputs.append(
          document.importNode(input, true),
        );
      });

    currentForm.prepend(hiddenInputs);

    const currentSelect =
      currentForm.querySelector(selectors.sortSelect);

    const sourceSelect =
      sourceForm.querySelector(selectors.sortSelect);

    if (currentSelect && sourceSelect) {
      currentSelect.value = sourceSelect.value;
    }
  };

  const replaceResultsContent = (root, sourceRoot) => {
    const currentMain =
      root.querySelector(selectors.catalogMain);

    const sourceMain =
      sourceRoot.querySelector(selectors.catalogMain);

    if (!currentMain || !sourceMain) {
      throw new Error(
        'Collection results region was missing.',
      );
    }

    currentMain.replaceWith(
      document.importNode(sourceMain, true),
    );

    syncProductCount(root, sourceRoot);
    syncFilterTrigger(root, sourceRoot);
    syncFilterForm(root, sourceRoot);
    syncSortForm(root, sourceRoot);
    formatNumbers(root);
    syncCardRatings(root);
  };

  const replaceCollectionSection =
    async (
      root,
      targetUrl,
      {
        historyMode = 'push',
        focusResults = true,
        scrollBeforeUpdate = false,
      } = {},
    ) => {
      const cleanUrl =
        getCleanUrl(targetUrl);

      const sectionId =
        root?.dataset.sectionId;

      if (!root || !cleanUrl || !sectionId) {
        window.location.assign(
          cleanUrl?.toString() ||
          targetUrl,
        );

        return;
      }

      root.kscRequestController?.abort();

      const requestController =
        new AbortController();

      root.kscRequestController =
        requestController;

      closeFilter(root, false);

      if (scrollBeforeUpdate) {
        await scrollToCollectionToolbar(root);
      }

      setLoading(root, true);

      try {
        const requestUrl =
          getSectionUrl(
            cleanUrl,
            sectionId,
          );

        if (!requestUrl) {
          throw new Error(
            'Collection URL could not be created.',
          );
        }

        const response = await fetch(
          requestUrl,
          {
            headers: {
              Accept: 'text/html',
            },
            credentials: 'same-origin',
            signal:
              requestController.signal,
          },
        );

        if (!response.ok) {
          throw new Error(
            `Collection request failed: ${response.status}`,
          );
        }

        const html =
          await response.text();

        const parsedDocument =
          new DOMParser().parseFromString(
            html,
            'text/html',
          );

        const sourceRoot =
          parsedDocument.querySelector(
            selectors.section,
          );

        if (!sourceRoot) {
          throw new Error(
            'Collection section was missing.',
          );
        }

        replaceResultsContent(root, sourceRoot);
        setFilterPageState(false);

        if (historyMode === 'push') {
          window.history.pushState(
            {
              kscUrl:
                cleanUrl.toString(),
            },
            '',
            cleanUrl,
          );
        } else if (
          historyMode === 'replace'
        ) {
          window.history.replaceState(
            {
              kscUrl:
                cleanUrl.toString(),
            },
            '',
            cleanUrl,
          );
        }

        announceProductsUpdated(
          root,
          {
            mode: 'replace',
          },
        );

        if (focusResults) {
          window.requestAnimationFrame(() => {
            root
              .querySelector(
                selectors.productCount,
              )
              ?.focus({
                preventScroll: true,
              });
          });
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        console.warn(
          'Kawaii Stop collection update failed.',
          error,
        );

        window.location.assign(
          cleanUrl.toString(),
        );
      } finally {
        if (document.contains(root)) {
          setLoading(root, false);
        }
      }
    };

  const initializeFilterForm = (
    root,
    signal,
  ) => {
    const form =
      root.querySelector(selectors.filterForm);

    if (!form) return;

    form.addEventListener(
      'submit',
      (event) => {
        const url = getFormUrl(form);

        if (!url) return;

        event.preventDefault();

        replaceCollectionSection(
          root,
          url,
          {
            scrollBeforeUpdate: true,
          },
        );
      },
      { signal },
    );
  };

  const initializeSort = (
    root,
    signal,
  ) => {
    const form =
      root.querySelector(selectors.sortForm);

    const select =
      root.querySelector(selectors.sortSelect);

    if (!form || !select) return;

    select.addEventListener(
      'change',
      () => {
        const url = getFormUrl(form);

        if (!url) {
          form.requestSubmit();
          return;
        }

        replaceCollectionSection(
          root,
          url,
        );
      },
      { signal },
    );
  };

  const initializeAjaxLinks = (
    root,
    signal,
  ) => {
    root.addEventListener(
      'click',
      (event) => {
        const link =
          event.target.closest(
            selectors.ajaxLink,
          );

        if (
          !link ||
          !root.contains(link)
        ) {
          return;
        }

        const url =
          getCleanUrl(
            link.getAttribute('href'),
          );

        if (!url) return;

        event.preventDefault();

        replaceCollectionSection(
          root,
          url,
          {
            scrollBeforeUpdate: true,
          },
        );
      },
      { signal },
    );
  };

  const initializeLoadMore = (
    root,
    signal,
  ) => {
    root.addEventListener(
      'click',
      async (event) => {
        const button =
          event.target.closest(
            selectors.loadMore,
          );

        if (
          !button ||
          !root.contains(button)
        ) {
          return;
        }

        event.preventDefault();

        if (
          button.classList.contains(
            'is-loading',
          )
        ) {
          return;
        }

        const grid =
          root.querySelector(selectors.grid);

        const sectionId =
          root.dataset.sectionId;

        const nextUrl =
          button.getAttribute('href');

        const label =
          button.querySelector(
            selectors.loadMoreLabel,
          );

        if (
          !grid ||
          !sectionId ||
          !nextUrl
        ) {
          window.location.assign(
            nextUrl ||
            window.location.href,
          );

          return;
        }

        button.classList.add(
          'is-loading',
        );

        button.setAttribute(
          'aria-busy',
          'true',
        );

        if (label) {
          label.textContent = 'Loading…';
        }

        try {
          const requestUrl =
            getSectionUrl(
              nextUrl,
              sectionId,
            );

          if (!requestUrl) {
            throw new Error(
              'Load-more URL could not be created.',
            );
          }

          const response = await fetch(
            requestUrl,
            {
              headers: {
                Accept: 'text/html',
              },
              credentials: 'same-origin',
              signal,
            },
          );

          if (!response.ok) {
            throw new Error(
              `Load-more request failed: ${response.status}`,
            );
          }

          const html =
            await response.text();

          const parsedDocument =
            new DOMParser().parseFromString(
              html,
              'text/html',
            );

          const sourceRoot =
            parsedDocument.querySelector(
              selectors.section,
            );

          const sourceGrid =
            sourceRoot?.querySelector(
              selectors.grid,
            );

          if (!sourceRoot || !sourceGrid) {
            throw new Error(
              'Load-more products were missing.',
            );
          }

          const newItems = [
            ...sourceGrid.querySelectorAll(
              selectors.gridItem,
            ),
          ];

          const fragment =
            document.createDocumentFragment();

          newItems.forEach((item) => {
            fragment.append(
              document.importNode(
                item,
                true,
              ),
            );
          });

          const results =
            root.querySelector(
              selectors.results,
            );

          const previousLoaded =
            Number(
              results?.dataset
                .loadedProducts || 0,
            );

          grid.append(fragment);

          updatePagination(
            root,
            sourceRoot,
          );

          updateProgress(
            root,
            previousLoaded +
              newItems.length,
          );

          syncCardRatings(root);

          announceProductsUpdated(
            root,
            {
              mode: 'append',
            },
          );
        } catch (error) {
          if (error.name === 'AbortError') {
            return;
          }

          console.warn(
            'Kawaii Stop Load More failed.',
            error,
          );

          window.location.assign(nextUrl);
        } finally {
          button.classList.remove(
            'is-loading',
          );

          button.removeAttribute(
            'aria-busy',
          );

          if (
            label &&
            document.contains(label)
          ) {
            label.textContent = 'Show more';
          }
        }
      },
      { signal },
    );
  };

  function initializeSection(root) {
    if (
      !root ||
      root.dataset.kscInitialized === 'true'
    ) {
      return;
    }

    root.dataset.kscInitialized = 'true';

    const controller =
      new AbortController();

    root.kscController = controller;

    formatNumbers(root);


    initializeFilterDrawer(
      root,
      controller.signal,
    );

    initializeFilterForm(
      root,
      controller.signal,
    );

    initializeSort(
      root,
      controller.signal,
    );

    initializeAjaxLinks(
      root,
      controller.signal,
    );

    initializeLoadMore(
      root,
      controller.signal,
    );

    initializeCardRatings(
      root,
      controller.signal,
    );
  }

  const initialize = (
    scope = document,
  ) => {
    scope
      .querySelectorAll?.(
        selectors.section,
      )
      .forEach(initializeSection);

    const descriptionController =
      new AbortController();

    initializeDescriptions(
      scope,
      descriptionController.signal,
    );
  };

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialize(),
      { once: true },
    );
  } else {
    initialize();
  }

  window.addEventListener(
    'popstate',
    () => {
      const root =
        document.querySelector(
          selectors.section,
        );

      if (!root) return;

      replaceCollectionSection(
        root,
        window.location.href,
        {
          historyMode: 'none',
          focusResults: false,
        },
      );
    },
  );

  window.addEventListener(
    'pageshow',
    () => {
      const root =
        document.querySelector(
          selectors.section,
        );

      if (!root) return;

      formatNumbers(root);
      syncFilterAccessibility(root);
      syncCardRatings(root);
    },
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initialize(event.target);
    },
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      event.target
        .querySelectorAll?.(
          selectors.section,
        )
        .forEach((root) => {
          root.kscRequestController
            ?.abort();

          root.kscController
            ?.abort();
        });

      setFilterPageState(false);
    },
  );
})();
